/**
 * One-click email unsubscribe landing page — hosted at /unsubscribe, NO
 * sign-in. Sequence-email footers link here with ?cid=<candidate>&sig=<hmac>;
 * on load we POST the pair to the public API, which verifies the signature
 * and sets email_opt_out. No confirmation step — one click means one click.
 *
 * Phase 2 design system: Card + Icon, token-driven so it themes with the
 * tenant brand and follows the OS light/dark preference.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Icon } from '../components/ui';

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined)
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : '/api/v1';

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const [state, setState] = useState<'working' | 'done' | 'invalid'>('working');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const cid = params.get('cid') ?? '';
    const sig = params.get('sig') ?? '';
    if (!cid || !sig) { setState('invalid'); return; }
    fetch(`${API_BASE}/public/unsubscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cid, sig }),
    })
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (r.ok) { setMessage(body?.message ?? ''); setState('done'); }
        else setState('invalid');
      })
      .catch(() => setState('invalid'));
  }, [params]);

  return (
    <div style={page}>
      <div style={{ width: '100%', maxWidth: 480 }}>
        <Card>
          <div style={{ textAlign: 'center' }}>
            {state === 'working' && <p style={{ color: 'var(--t3)', fontSize: 14, margin: 0 }}>Working…</p>}
            {state === 'done' && (
              <>
                <div className="ui-empty__icon" style={{ margin: '0 auto 10px', background: 'var(--good-tint)', color: 'var(--good-fg)' }}>
                  <Icon name="check-circle" size={28} />
                </div>
                <h1 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 800, color: 'var(--good-fg)' }}>You're unsubscribed</h1>
                <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: 'var(--t2)' }}>
                  {message || 'You will no longer receive marketing emails from us.'} If this was a mistake,
                  reply to any previous email and we'll re-subscribe you.
                </p>
              </>
            )}
            {state === 'invalid' && (
              <>
                <div className="ui-empty__icon" style={{ margin: '0 auto 10px', background: 'var(--danger-tint)', color: 'var(--danger-fg)' }}>
                  <Icon name="alert" size={26} />
                </div>
                <h1 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 800, color: 'var(--t1)' }}>Link not valid</h1>
                <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: 'var(--t2)' }}>
                  This unsubscribe link is invalid or expired. Please use the link from a recent email.
                </p>
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

const page: React.CSSProperties = { minHeight: '100vh', background: 'var(--sf2)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '48px 16px' };
